import { useEffect, useMemo, useRef, useState } from 'react';
import { BY_SYMBOL, elementColor } from '../elements';
import {
  cueHighlights,
  cueIsActive,
  DARK_INK,
  NowYouTry,
  usePrefersReducedMotion,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  type VizCue,
} from '../vizShared';

// BondPolarity — pick two elements and see the electronegativity difference
// (ΔEN), the bond classification (nonpolar covalent / polar covalent / ionic)
// on a continuous scale, and a dipole arrow pointing toward the more
// electronegative atom (δ−). A native range slider scrubs ΔEN directly so the
// bond character morphs nonpolar ↔ polar ↔ ionic. Resilient default: H–Cl.
//
// Self-demo: on mount the ΔEN sweeps 0 → the selected bond's value, animating
// the classification band and dipole, then settles + "Now you try".
//
// cue: { progress? → set ΔEN slider position 0..1 (maps to 0..MAX);
//        highlight? → pick an example bond by name/symbol; step? → step examples }
//
// config: { a?, b?, title? }  (a/b are element symbols)

export interface BondPolarityConfig {
  title?: string;
  a?: string; // element symbol
  b?: string; // element symbol
  atoms?: string[]; // alias for [a, b] — tolerated so {atoms:["C","O"]} works too
}

interface Props {
  config?: BondPolarityConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// Pauling electronegativities. Noble gases / a few elements without a standard
// Pauling value are omitted; the selector only offers elements present here.
const EN: Record<string, number> = {
  H: 2.20,
  Li: 0.98, Be: 1.57, B: 2.04, C: 2.55, N: 3.04, O: 3.44, F: 3.98,
  Na: 0.93, Mg: 1.31, Al: 1.61, Si: 1.90, P: 2.19, S: 2.58, Cl: 3.16,
  K: 0.82, Ca: 1.00, Sc: 1.36, Ti: 1.54, V: 1.63, Cr: 1.66, Mn: 1.55,
  Fe: 1.83, Co: 1.88, Ni: 1.91, Cu: 1.90, Zn: 1.65, Ga: 1.81, Ge: 2.01,
  As: 2.18, Se: 2.55, Br: 2.96,
  Rb: 0.82, Sr: 0.95, Ag: 1.93, Sn: 1.96, Sb: 2.05, Te: 2.10, I: 2.66,
  Cs: 0.79, Ba: 0.89, Pt: 2.28, Au: 2.54, Pb: 2.33, Bi: 2.02,
};

// Element symbols offered in the pickers, ordered by Z for a sensible list.
const OPTIONS = Object.keys(EN).sort(
  (a, b) => (BY_SYMBOL[a]?.z ?? 999) - (BY_SYMBOL[b]?.z ?? 999),
);

// Quick example bonds spanning the classification range (for cue.step / chips).
const EXAMPLES: Array<{ label: string; a: string; b: string }> = [
  { label: 'Cl–Cl', a: 'Cl', b: 'Cl' },
  { label: 'H–Cl', a: 'H', b: 'Cl' },
  { label: 'H–O', a: 'H', b: 'O' },
  { label: 'C–O', a: 'C', b: 'O' },
  { label: 'Na–Cl', a: 'Na', b: 'Cl' },
];

// Classify a bond by ΔEN using the common teaching thresholds.
function classify(d: number): { label: string; color: string } {
  if (d < 0.4) return { label: 'Nonpolar covalent', color: 'var(--ws-success)' };
  if (d < 1.7) return { label: 'Polar covalent', color: 'var(--ws-warning)' };
  return { label: 'Ionic', color: 'var(--ws-danger)' };
}

const W = 360;
const H = 150;
const MAX = 3.3; // ΔEN axis maximum

export default function BondPolarity({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // Accept either {a,b} or the {atoms:[a,b]} alias (the latter is what several
  // authored configs use). Fall back to a sensible H–Cl only if neither is valid.
  const cfgA = config.a ?? config.atoms?.[0];
  const cfgB = config.b ?? config.atoms?.[1];
  const [a, setA] = useState(() => (cfgA && EN[cfgA] != null ? cfgA : 'H'));
  const [b, setB] = useState(() => (cfgB && EN[cfgB] != null ? cfgB : 'Cl'));
  const interactedRef = useRef(false);
  const rafRef = useRef<number>(0);

  const enA = EN[a] ?? 0;
  const enB = EN[b] ?? 0;
  const trueDelta = useMemo(() => Math.abs(enA - enB), [enA, enB]);

  // The displayed ΔEN. During the self-demo / when scrubbing the slider this can
  // differ from the atoms' true ΔEN. Picking atoms re-syncs it.
  const [delta, setDelta] = useState<number>(reduced || hasCue ? trueDelta : 0);
  const [demoDone, setDemoDone] = useState(reduced || hasCue);

  const markInteracted = () => {
    cancelAnimationFrame(rafRef.current);
    if (!demoDone) setDemoDone(true);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'bond_polarity', a, b });
    }
  };

  // Self-demo: ease ΔEN from 0 up to the bond's true value on mount.
  useEffect(() => {
    if (reduced || hasCue) {
      setDelta(trueDelta);
      return;
    }
    const start = performance.now();
    const dur = 1500;
    const from = 0;
    const to = trueDelta;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDelta(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDemoDone(true);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Cue handling (supersedes the self-demo) -----
  const highlights = useMemo(() => cueHighlights(cue), [cue]);
  // cue.highlight → pick an example bond by label or either symbol.
  useEffect(() => {
    if (highlights.length === 0) return;
    const match = EXAMPLES.find(
      (ex) =>
        highlights.includes(ex.label.toLowerCase()) ||
        highlights.includes(ex.label.replace('–', '').toLowerCase()) ||
        (highlights.includes(ex.a.toLowerCase()) && highlights.includes(ex.b.toLowerCase())),
    );
    if (match) {
      setA(match.a);
      setB(match.b);
      setDelta(Math.abs((EN[match.a] ?? 0) - (EN[match.b] ?? 0)));
    }
  }, [highlights]);
  // cue.step → step through examples.
  useEffect(() => {
    if (cue?.step == null) return;
    const i = ((Math.round(cue.step) % EXAMPLES.length) + EXAMPLES.length) % EXAMPLES.length;
    const ex = EXAMPLES[i];
    setA(ex.a);
    setB(ex.b);
    setDelta(Math.abs((EN[ex.a] ?? 0) - (EN[ex.b] ?? 0)));
  }, [cue?.step]);
  // cue.progress (0..1) → scrub ΔEN directly.
  useEffect(() => {
    if (cue?.progress == null) return;
    setDelta(Math.max(0, Math.min(1, cue.progress)) * MAX);
  }, [cue?.progress]);

  const cls = classify(delta);
  // Which atom is more electronegative? It carries δ−; the dipole points to it.
  // While scrubbing a synthetic ΔEN, fall back to the atom EN ordering for sign.
  const bMoreEN = enB > enA;
  const same = delta < 0.001;

  const selectAtoms = (na: string, nb: string) => {
    setA(na);
    setB(nb);
    setDelta(Math.abs((EN[na] ?? 0) - (EN[nb] ?? 0)));
    markInteracted();
  };

  // Scale: map ΔEN 0..MAX onto the bar.
  const barX = 24;
  const barW = W - 48;
  const frac = Math.min(1, delta / MAX);
  const markerX = barX + frac * barW;

  // Atom positions for the bond drawing.
  const ay = 48;
  const ax1 = W / 2 - 60;
  const ax2 = W / 2 + 60;

  // Dipole magnitude scales the arrow length with ΔEN for a tactile feel.
  const dipoleScale = Math.min(1, delta / MAX);
  const sliderTrans = reduced ? undefined : 'left 120ms linear';

  return (
    <VizCard
      eyebrow="Bonding · Polarity"
      title={config.title ?? 'Bond polarity & electronegativity'}
      subtitle="ΔEN sets the bond character: nonpolar ↔ polar ↔ ionic"
      reducedMotion={reduced}
      hint="Pick two atoms or drag the ΔEN slider · examples below · sweeps itself on load"
    >
      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row">
          {/* Bond + dipole drawing */}
          <div className="relative flex-shrink-0">
            <NowYouTry show={demoDone && !hasCue && !reduced} />
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full max-w-[360px] rounded-md border border-ws-border bg-ws-surface-2"
              role="img"
              aria-label={`Bond between ${a} and ${b}, ΔEN ${delta.toFixed(2)}, ${cls.label}`}
            >
              {/* Bond line */}
              <line x1={ax1} y1={ay} x2={ax2} y2={ay} stroke="var(--ws-ink-3)" strokeWidth="3" />

              {/* Partial-charge labels (skip if effectively nonpolar) */}
              {!same && (
                <>
                  <text x={ax1} y={ay - 26} textAnchor="middle" fontSize="12" fontWeight="700" fill={bMoreEN ? 'var(--ws-danger)' : 'var(--ws-accent)'}>
                    {bMoreEN ? 'δ+' : 'δ−'}
                  </text>
                  <text x={ax2} y={ay - 26} textAnchor="middle" fontSize="12" fontWeight="700" fill={bMoreEN ? 'var(--ws-accent)' : 'var(--ws-danger)'}>
                    {bMoreEN ? 'δ−' : 'δ+'}
                  </text>
                </>
              )}

              {/* Atoms */}
              <circle cx={ax1} cy={ay} r="20" fill={elementColor(a)} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
              <text x={ax1} y={ay + 5} textAnchor="middle" fontSize="14" fontWeight="800" style={{ fill: DARK_INK }}>{a}</text>
              <circle cx={ax2} cy={ay} r="20" fill={elementColor(b)} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
              <text x={ax2} y={ay + 5} textAnchor="middle" fontSize="14" fontWeight="800" style={{ fill: DARK_INK }}>{b}</text>

              {/* Dipole arrow (cross-and-arrow): tail = δ+, head = δ−). Length ∝ ΔEN. */}
              {!same && (() => {
                const fromX = bMoreEN ? ax1 : ax2;
                const toX = bMoreEN ? ax2 : ax1;
                const dir = toX > fromX ? 1 : -1;
                const tailX = fromX + dir * 26;
                const fullHeadX = toX - dir * 26;
                const headX = tailX + (fullHeadX - tailX) * dipoleScale;
                const yy = ay + 40;
                return (
                  <g
                    stroke="var(--ws-accent)"
                    strokeWidth="2"
                    fill="var(--ws-accent)"
                    style={reduced ? undefined : { transition: 'opacity 200ms ease-out' }}
                  >
                    {/* plus sign at the tail */}
                    <line x1={tailX - dir * 5} y1={yy - 5} x2={tailX - dir * 5} y2={yy + 5} />
                    <line x1={tailX - dir * 9} y1={yy} x2={tailX - dir * 1} y2={yy} />
                    {/* shaft */}
                    <line x1={tailX} y1={yy} x2={headX} y2={yy} />
                    {/* arrowhead */}
                    <polygon
                      points={`${headX},${yy} ${headX - dir * 8},${yy - 4} ${headX - dir * 8},${yy + 4}`}
                      stroke="none"
                    />
                  </g>
                );
              })()}
              {same && (
                <text x={W / 2} y={ay + 44} textAnchor="middle" fontSize="11" fill="var(--ws-ink-3)">
                  No dipole (equal sharing)
                </text>
              )}
            </svg>
          </div>

          {/* Selectors + readout */}
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex gap-2">
              <Picker label="Atom A" value={a} onChange={(v) => selectAtoms(v, b)} />
              <Picker label="Atom B" value={b} onChange={(v) => selectAtoms(a, v)} />
            </div>

            {/* ΔEN scrubber (native range — keyboard accessible) */}
            <div>
              <label className="mb-1 flex items-center justify-between text-[10px] font-medium uppercase tracking-wide text-ws-ink-3" htmlFor="delta-en">
                <span>ΔEN scrubber</span>
                <span className="font-mono text-ws-ink-2">{delta.toFixed(2)}</span>
              </label>
              <input
                id="delta-en"
                type="range"
                min={0}
                max={MAX}
                step={0.01}
                value={Math.min(MAX, delta)}
                onChange={(e) => { setDelta(parseFloat(e.target.value)); markInteracted(); }}
                aria-label="Electronegativity difference"
                className={`w-full accent-ws-accent ${VIZ_FOCUS}`}
              />
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-sm border border-ws-border bg-ws-tile p-2 text-xs text-ws-ink-2">
              <div><dt className="inline text-ws-ink-3">EN({a}): </dt><dd className="inline font-medium">{enA.toFixed(2)}</dd></div>
              <div><dt className="inline text-ws-ink-3">EN({b}): </dt><dd className="inline font-medium">{enB.toFixed(2)}</dd></div>
              <div className="col-span-2"><dt className="inline text-ws-ink-3">ΔEN: </dt><dd className="inline font-semibold text-ws-ink">{delta.toFixed(2)}</dd></div>
              <div className="col-span-2">
                <dt className="inline text-ws-ink-3">Bond type: </dt>
                <dd className="inline font-semibold" style={{ color: cls.color }}>{cls.label}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Example bonds */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">Examples</span>
          {EXAMPLES.map((ex) => {
            const active = a === ex.a && b === ex.b;
            return (
              <button
                key={ex.label}
                type="button"
                onClick={() => selectAtoms(ex.a, ex.b)}
                aria-pressed={active}
                className={`${VIZ_BTN_GHOST} !px-2 !py-1 font-mono ${active ? '!bg-ws-accent !text-ws-on-accent !border-ws-accent' : ''}`}
              >
                {ex.label}
              </button>
            );
          })}
        </div>

        {/* Classification scale */}
        <div className="relative">
          <svg viewBox={`0 0 ${W} 56`} className="w-full" role="img" aria-label="Bond polarity scale">
            {/* gradient bands */}
            <rect x={barX} y="14" width={barW * (0.4 / MAX)} height="12" fill="var(--ws-success)" opacity="0.7" rx="2" />
            <rect x={barX + barW * (0.4 / MAX)} y="14" width={barW * (1.3 / MAX)} height="12" fill="var(--ws-warning)" opacity="0.7" />
            <rect x={barX + barW * (1.7 / MAX)} y="14" width={barW * (1.6 / MAX)} height="12" fill="var(--ws-danger)" opacity="0.7" rx="2" />
            {/* ticks */}
            <text x={barX} y="44" fontSize="9" fill="var(--ws-ink-3)" textAnchor="middle">0</text>
            <text x={barX + barW * (0.4 / MAX)} y="44" fontSize="9" fill="var(--ws-ink-3)" textAnchor="middle">0.4</text>
            <text x={barX + barW * (1.7 / MAX)} y="44" fontSize="9" fill="var(--ws-ink-3)" textAnchor="middle">1.7</text>
            <text x={barX + barW} y="44" fontSize="9" fill="var(--ws-ink-3)" textAnchor="middle">{MAX}</text>
            {/* marker */}
            <g style={sliderTrans ? { transition: 'transform 120ms linear' } : undefined} transform={`translate(${markerX} 0)`}>
              <polygon points={`0,10 -5,2 5,2`} fill="var(--ws-ink)" />
              <line x1={0} y1="10" x2={0} y2="30" stroke="var(--ws-ink)" strokeWidth="2" />
            </g>
          </svg>
        </div>
      </div>
    </VizCard>
  );
}

function Picker({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex-1">
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-sm border border-ws-border bg-ws-tile px-2 py-1.5 text-sm font-medium text-ws-ink transition-colors hover:border-ws-border-strong ${VIZ_FOCUS}`}
      >
        {OPTIONS.map((sym) => (
          <option key={sym} value={sym}>{sym} ({EN[sym].toFixed(2)})</option>
        ))}
      </select>
    </div>
  );
}
